import React, {useCallback, useEffect, useState} from 'react';
import {Box, MenuItem, TextField} from '@mui/material';
import PropTypes from 'prop-types';
import EditMultiFieldsBuilder from './Shared/EditMultiFieldsBuilder';
import RichEditorResultText from './Shared/RichEditorResultText';

const Dropdown = ({
                      index,
                      componentId,
                      value,
                      onChange,
                      descriptionHtml,
                      onDescriptionHtmlChange,
                      commitDescriptionHtml,
                      isEditMode,
                      enableValidation,
                      options,
                      isFormArchived,
                      relatedComponent,
                      readOnly,
                      placeholderValues,
                      accessLevel,
                      error,
                      audience,
                      recipientType,
                      submitMode
                  }) => {
    const [draftDescriptionHtml, setDraftDescriptionHtml] = useState(descriptionHtml || '');
    const [isEditorFocused, setIsEditorFocused] = useState(false);

    useEffect(() => {
        if (isEditorFocused) return;
        setDraftDescriptionHtml(descriptionHtml || '');
    }, [descriptionHtml, isEditorFocused]);

    const handleDraftChange = useCallback((nextHtml) => {
        setDraftDescriptionHtml(nextHtml);
        onDescriptionHtmlChange(nextHtml);
    }, [onDescriptionHtmlChange]);

    const handleFocus = useCallback(() => {
        setIsEditorFocused(true);
    }, []);

    const handleBlur = useCallback(() => {
        setIsEditorFocused(false);
        commitDescriptionHtml();
    }, [commitDescriptionHtml]);

    return (
        <Box sx={{mt: 2}}>
            {isEditMode
                ? (
                    <EditMultiFieldsBuilder
                        onChange={onChange}
                        enableValidation={enableValidation}
                        description={descriptionHtml}
                        index={index}
                        options={options}
                        isFormArchived={isFormArchived}
                        optionLengthLimit={180}
                        relatedComponent={relatedComponent}
                        accessLevel={accessLevel}
                        currentDescription={draftDescriptionHtml}
                        setCurrentDescription={handleDraftChange}
                        audience={audience}
                        recipientType={recipientType}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                    />
                )
                : (
                    <>
                        <RichEditorResultText
                            id={componentId}
                            description={descriptionHtml}
                            placeholderValues={placeholderValues}
                            readOnly={readOnly}
                            enableValidation={enableValidation}
                            audience={audience}
                            recipientType={recipientType}
                            submitMode={submitMode}
                        />
                        <Box
                            key={`${descriptionHtml}-${value}`}
                            display="flex"
                            justifyContent="center"
                        >
                            <TextField
                                sx={{backgroundColor: '#ffffff'}}
                                select
                                value={value}
                                fullWidth
                                onChange={e =>
                                    onChange({
                                        id: componentId,
                                        radioValue: e.target.value,
                                    })
                                }
                                error={!!error}
                                helperText={error}
                                SelectProps={{
                                    MenuProps: {
                                        autoFocus: false,
                                        disableAutoFocusItem: true,
                                        disableEnforceFocus: true,
                                        disableAutoFocus: true,
                                    },
                                }}
                            >
                                {options?.map(option => (
                                    <MenuItem
                                        key={option.id}
                                        value={option.id}
                                        sx={{
                                            whiteSpace: 'normal',
                                            wordBreak: 'break-word',
                                        }}
                                    >
                                    {option.description}
                                </MenuItem>
                                ))}
                            </TextField>
                        </Box>
                    </>
                )}
        </Box>
    );
};

Dropdown.propTypes = {
    componentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    value: PropTypes.string,
    index: PropTypes.number,
    options: PropTypes.array,
    descriptionHtml: PropTypes.string,
    onDescriptionHtmlChange: PropTypes.func,
    commitDescriptionHtml: PropTypes.func,
    enableValidation: PropTypes.bool,
    onChange: PropTypes.func,
    isEditMode: PropTypes.bool,
    isFormArchived: PropTypes.bool,
    relatedComponent: PropTypes.object,
    readOnly: PropTypes.bool,
    placeholderValues: PropTypes.object,
    accessLevel: PropTypes.number,
    error: PropTypes.string,
    audience: PropTypes.number,
    recipientType: PropTypes.string,
    submitMode: PropTypes.bool
};

export default Dropdown;
